import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Camera, Upload, FileText, ScanLine, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { ocrAnalyze } from "@/lib/api";

const toDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

export const UploadStep = ({ onComplete, onManual }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);

    const handleFile = async (f) => {
        if (!f) return;
        setFile(f);
        if (f.type.startsWith("image/")) {
            setPreview(await toDataUrl(f));
        } else {
            setPreview(null);
        }
    };

    const analyse = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const dataUrl = await toDataUrl(file);
            const base64 = dataUrl.split(",")[1];
            const res = await ocrAnalyze({ file_base64: base64, mime_type: file.type || "image/jpeg" });
            if (res.success) {
                toast.success("Fahrzeugdaten erkannt!");
            } else {
                toast.warning("Konnte nicht alles lesen – bitte Werte prüfen.");
            }
            onComplete(res.vehicle);
        } catch (e) {
            toast.error("OCR-Analyse fehlgeschlagen. Bitte Daten manuell eingeben.");
            onManual();
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto max-w-xl"
        >
            <div className="text-center">
                <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">
                    Zulassungsschein hochladen
                </h2>
                <p className="mt-2 text-sm text-[#A1A1AA]">
                    Foto aufnehmen oder Bild / PDF hochladen. Unsere KI liest die Fahrzeugdaten automatisch aus.
                </p>
            </div>

            {/* Upload zone */}
            <div className="relative mt-8 overflow-hidden rounded-2xl border-2 border-dashed border-border bg-surface p-6">
                {loading && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#0A0A0A]/90">
                        <div className="relative h-40 w-full max-w-[260px] overflow-hidden rounded-lg border border-primary/30">
                            {preview ? (
                                <img src={preview} alt="" className="h-full w-full object-cover opacity-70" />
                            ) : (
                                <div className="flex h-full items-center justify-center">
                                    <FileText className="h-10 w-10 text-primary/50" />
                                </div>
                            )}
                            <div className="scan-overlay animate-scan-line" />
                        </div>
                        <div className="mt-5 flex items-center gap-2 text-primary">
                            <ScanLine className="h-5 w-5 animate-pulse" />
                            <span className="text-sm font-medium">KI liest Fahrzeugdaten...</span>
                        </div>
                    </div>
                )}

                {!preview && !file ? (
                    <div className="flex flex-col items-center py-6 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                            <Upload className="h-7 w-7 text-primary" />
                        </div>
                        <p className="mt-4 text-sm text-[#A1A1AA]">
                            Ziehe deine Datei hierher oder wähle eine Option
                        </p>
                    </div>
                ) : (
                    <div className="flex items-center gap-4 py-2">
                        {preview ? (
                            <img
                                src={preview}
                                alt="Vorschau"
                                className="h-20 w-20 rounded-lg object-cover"
                            />
                        ) : (
                            <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-surface-elevated">
                                <FileText className="h-8 w-8 text-primary" />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-white">{file?.name}</div>
                            <div className="text-xs text-[#A1A1AA]">
                                {((file?.size || 0) / 1024).toFixed(0)} KB
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setFile(null);
                                setPreview(null);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-[#A1A1AA] hover:text-white"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </div>

            <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="file-input"
            />
            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="camera-input"
            />

            <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                    data-testid="take-photo-btn"
                    onClick={() => cameraRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface-elevated px-4 py-3.5 text-sm font-medium text-white hover:border-primary/50 transition-colors"
                >
                    <Camera className="h-4 w-4" /> Foto aufnehmen
                </button>
                <button
                    data-testid="choose-file-btn"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface-elevated px-4 py-3.5 text-sm font-medium text-white hover:border-primary/50 transition-colors"
                >
                    <Upload className="h-4 w-4" /> Datei wählen
                </button>
            </div>

            <button
                data-testid="start-ocr-btn"
                disabled={!file || loading}
                onClick={analyse}
                className="mt-4 w-full rounded-md bg-primary px-6 py-4 text-base font-bold text-white red-glow hover:bg-[#FF3B30] transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            >
                {loading ? "Analysiere..." : "Jetzt analysieren"}
            </button>

            <button
                data-testid="manual-entry-btn"
                onClick={onManual}
                className="mt-4 flex w-full items-center justify-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
            >
                <Edit3 className="h-4 w-4" /> Daten lieber manuell eingeben
            </button>
        </motion.div>
    );
};

export default UploadStep;
